import React, { useState } from "react";
import { Button, Card, Checkbox, Form, Select, App } from "antd";
import { useNavigate } from "react-router-dom";
import { useGrades } from "../../../hooks/useGrades";
import { useSubjects } from "../../../hooks/useSubject";
import {
  useCreateOdReviewRunMutation,
  useGetOdReviewCoursesQuery,
} from "../../../store/api/odReviewApi";
import type { AxiosBaseQueryError } from "../../../store/api/axiosBaseQuery";
import type { OdReviewPhase } from "../../../services/odReview/odReview.types";
import {
  DEFAULT_RUN_PHASES,
  OD_REVIEW_PHASE_LABELS,
  normalizeRunPhases,
} from "./RunLauncher.utils";

interface RunLauncherFormValues {
  gradeId: number;
  subjectId: number;
  courseIds: number[];
  phases: OdReviewPhase[];
}

const PHASE_OPTIONS = [
  {
    label: OD_REVIEW_PHASE_LABELS.text,
    value: "text" as OdReviewPhase,
    disabled: true,
  },
  { label: OD_REVIEW_PHASE_LABELS.resources, value: "resources" as OdReviewPhase },
  { label: OD_REVIEW_PHASE_LABELS.photos, value: "photos" as OdReviewPhase },
];

const RunLauncher: React.FC = () => {
  const { notification } = App.useApp();
  const [form] = Form.useForm<RunLauncherFormValues>();
  const navigate = useNavigate();
  const { grades } = useGrades();
  const { subjects } = useSubjects();
  const [gradeId, setGradeId] = useState<number | null>(null);
  const [subjectId, setSubjectId] = useState<number | null>(null);
  const [createOdReviewRun, { isLoading: isCreating }] =
    useCreateOdReviewRunMutation();

  const { data: courseOptions = [], isFetching: isLoadingCourses } =
    useGetOdReviewCoursesQuery(
      { grade_id: gradeId ?? undefined, subject_id: subjectId ?? undefined },
      { skip: gradeId === null || subjectId === null },
    );

  const gradeSelectOptions = (grades ?? [])
    .map((g) => ({ value: g.id, label: g.grade }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const subjectSelectOptions = (subjects ?? [])
    .filter((s) => !s.hidden)
    .map((s) => ({ value: s.id, label: s.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const courseSelectOptions = courseOptions.map((c) => ({
    value: c.course_id,
    label: c.course_title,
    title: [c.course_title, ...c.programs.map((p) => `Semana ${p.week}`)].join(
      " · ",
    ),
  }));

  const handleValuesChange = (changed: Partial<RunLauncherFormValues>) => {
    if ("gradeId" in changed) {
      setGradeId(changed.gradeId ?? null);
      setSubjectId(null);
      form.setFieldsValue({ subjectId: undefined, courseIds: [] });
    }
    if ("subjectId" in changed) {
      setSubjectId(changed.subjectId ?? null);
      form.setFieldsValue({ courseIds: [] });
    }
  };

  const handleSubmit = async (values: RunLauncherFormValues) => {
    try {
      const run = await createOdReviewRun({
        course_ids: values.courseIds,
        phases: normalizeRunPhases(values.phases),
      }).unwrap();
      form.resetFields();
      setGradeId(null);
      setSubjectId(null);
      navigate(`/revision-ods/${run.id}`);
    } catch (error) {
      const description =
        (error as AxiosBaseQueryError | undefined)?.data &&
        typeof (error as AxiosBaseQueryError).data === "object"
          ? ((error as AxiosBaseQueryError).data as { message?: string })
              .message
          : undefined;
      notification.error({
        message: "No se pudo iniciar la revisión",
        description: description ?? "Ocurrió un error al crear la corrida.",
        placement: "top",
      });
    }
  };

  return (
    <Card>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        requiredMark={false}
      >
        <Form.Item
          label="Grado"
          name="gradeId"
          rules={[{ required: true, message: "Seleccioná un grado" }]}
        >
          <Select
            placeholder="Seleccioná un grado"
            optionFilterProp="label"
            options={gradeSelectOptions}
          />
        </Form.Item>

        <Form.Item
          label="Asignatura"
          name="subjectId"
          rules={[{ required: true, message: "Seleccioná una asignatura" }]}
        >
          <Select
            placeholder="Seleccioná una asignatura"
            optionFilterProp="label"
            disabled={gradeId === null}
            options={subjectSelectOptions}
          />
        </Form.Item>

        <Form.Item
          label="Cursos"
          name="courseIds"
          rules={[{ required: true, message: "Seleccioná al menos un curso" }]}
        >
          <Select
            mode="multiple"
            placeholder="Seleccioná uno o más cursos"
            optionFilterProp="label"
            disabled={subjectId === null}
            loading={isLoadingCourses}
            options={courseSelectOptions}
          />
        </Form.Item>

        <Form.Item
          label="Alcance de la revisión"
          name="phases"
          initialValue={DEFAULT_RUN_PHASES}
        >
          <Checkbox.Group options={PHASE_OPTIONS} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={isCreating}>
            Revisar secuencias
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default RunLauncher;
